import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import { ShieldCheck, Sparkles, Activity, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { useT, LanguageSwitcher } from "@/lib/i18n";
import { useState } from "react";
import { useAppSettings } from "@/lib/appSettings";

export default function Login() {
  const { user, loading, login } = useAuth();
  const { t } = useT();
  const s = useAppSettings();
  const [params] = useSearchParams();
  const authError = params.get("err");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(authError || "");
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to sign in. Please check your email and password.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col">
      <div className="grid lg:grid-cols-2 flex-1">
        {/* Left panel */}
        <div className="relative flex flex-col justify-between p-10 lg:p-16 border-r border-slate-200 bg-gradient-to-br from-slate-50 to-white">
          <div className="flex items-center justify-between gap-3">
            <img src="/kinos-logo.jpg" alt="KINOS logo" className="h-28 w-auto object-contain" data-testid="brand-logo-login" />
            <LanguageSwitcher />
          </div>

          <div className="max-w-lg">
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-red-600 mb-6" data-testid="login-strap-top">{t("login_strap_top")}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] tracking-tight text-slate-900" data-testid="login-headline">
              {s.login_headline_1 || t("login_headline_line1")}<br />
              <span className="text-red-600">{s.login_headline_2 || t("login_headline_line2")}</span>
            </h1>
            <p className="mt-6 text-slate-600 text-base leading-relaxed max-w-md" data-testid="login-tagline">
              {s.login_tagline || t("login_tagline")}
            </p>
            <p className="mt-4 text-sm text-red-600 max-w-md font-medium" data-testid="login-strap">
              {t("login_strap_bottom")}
            </p>

            <div className="mt-10 space-y-3">
              <Feature icon={<Sparkles className="h-4 w-4 text-red-600" strokeWidth={1.75} />} label={t("login_feature_ai")} />
              <Feature icon={<Activity className="h-4 w-4 text-red-600" strokeWidth={1.75} />} label={t("login_feature_sla")} />
              <Feature icon={<ShieldCheck className="h-4 w-4 text-red-600" strokeWidth={1.75} />} label={t("login_feature_roles")} />
            </div>
          </div>

          <div className="text-xs font-mono text-slate-500">© {new Date().getFullYear()} {s.company_name || "KINOS"} · {s.company_tagline || "Leading Innovation Technology"} · Rua Fatuhada, Dili, Timor-Leste</div>
        </div>

        {/* Right panel */}
        <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
          <div className="w-full max-w-md">
            <div className="border border-slate-200 bg-white p-10 shadow-xl shadow-slate-200/50 rounded-md">
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-red-600 mb-3">{t("login_signin")}</div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">{t("login_access_workspace")}</h2>
              <p className="mt-3 text-sm text-slate-600">{t("login_subtitle")}</p>

              {error && (
                <div
                  className="mt-6 flex items-start gap-2 border border-red-200 bg-red-50 rounded-sm p-3 text-xs text-red-700"
                  data-testid="login-error"
                >
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                  <div>{error}</div>
                </div>
              )}

              <form onSubmit={handleLogin} className="mt-8 space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-11 border border-slate-300 rounded-sm px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-11 border border-slate-300 rounded-sm px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" />
                </div>
                <Button type="submit" disabled={submitting} data-testid="local-login-btn" className="w-full h-12 bg-red-600 hover:bg-red-500 text-white rounded-sm font-semibold text-sm shadow-sm shadow-red-600/25">
                  {submitting ? "Signing in..." : "Sign in to KINOS"}
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t border-slate-200 rounded-sm bg-red-50/50 border border-red-200 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-600" strokeWidth={1.75} />
                  <div className="text-[11px] leading-relaxed text-slate-700">
                    <div className="font-mono uppercase tracking-[0.2em] text-red-700 text-[10px] mb-1">Unauthorized Access Warning</div>
                    This system is restricted to authorized KINOS personnel and pre-approved users only. Any unauthorized access, attempted access, or misuse will be logged, investigated, and prosecuted to the fullest extent of Timor-Leste law and applicable international regulations. By continuing, you consent to activity monitoring and audit logging.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer variant="login" />
    </div>
  );
}

function Feature({ icon, label }) {
  return (
    <div className="flex items-center gap-3 border border-slate-200 bg-white px-4 py-3 rounded-sm hover:border-red-200 hover:shadow-sm transition-all">
      <div className="h-8 w-8 border border-slate-200 flex items-center justify-center bg-red-50 rounded-sm">{icon}</div>
      <div className="text-sm text-slate-700 font-medium">{label}</div>
    </div>
  );
}
